import React, { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Zap, ShieldCheck, Tag, Headphones, ArrowRight, Sparkles, Clock, Gamepad2, Gamepad, Joystick, Monitor, Trophy, Gift, CreditCard, Crown, Star, Quote } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useI18n } from "../i18n";
import { Button } from "../components/ui/button";

const ICONS = { Gamepad2, Gamepad, Joystick, Monitor, Trophy, Gift, CreditCard, Crown };

function CountdownTimer() {
  const [time, setTime] = useState({ h: 8, m: 45, s: 32 });
  useEffect(() => {
    const id = setInterval(() => {
      setTime((prev) => {
        let s = prev.s - 1, m = prev.m, h = prev.h;
        if (s < 0) { s = 59; m -= 1; }
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 23; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const cell = (n) => String(n).padStart(2, "0");
  return (
    <div className="flex items-center gap-2 text-white" data-testid="countdown-timer">
      {[time.h, time.m, time.s].map((v, i) => (
        <React.Fragment key={i}>
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/10 backdrop-blur grid place-items-center font-display font-bold text-lg md:text-xl">{cell(v)}</div>
          {i < 2 && <span className="font-bold text-xl">:</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  const [featured, setFeatured] = useState([]);
  const [trending, setTrending] = useState([]);
  const [flashDeals, setFlashDeals] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/products?featured=true&limit=12"),
      api.get("/products?trending=true&limit=8"),
      api.get("/products?flash_deal=true&limit=8"),
      api.get("/products?new_release=true&limit=8"),
      api.get("/products?top_seller=true&limit=8"),
      api.get("/products?type=gift_card&limit=6"),
      api.get("/products?type=subscription&limit=6"),
      api.get("/categories"),
    ]).then(([f, tr, fd, nr, ts, gc, sb, c]) => {
      setFeatured(f.data.items);
      setTrending(tr.data.items);
      setFlashDeals(fd.data.items);
      setNewReleases(nr.data.items);
      setTopSellers(ts.data.items);
      setGiftCards(gc.data.items);
      setSubscriptions(sb.data.items);
      setCategories(c.data);
    }).catch(() => {});
  }, []);

  return (
    <div className="App" data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative bg-[#0F0F14] overflow-hidden noise" data-testid="hero-section">
        {/* Ambient lights */}
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        {/* Anime banner background blend (subtle, sits behind everything) */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/brand/gameflow-banner.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.14,
            filter: "saturate(1.1)",
            animation: "kenburns 50s ease-in-out infinite alternate",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F14]/40 via-[#0F0F14]/65 to-[#0F0F14] pointer-events-none" />
        <motion.div
          style={{ y: heroY }}
          className="absolute -top-40 -right-32 w-[600px] h-[600px] rounded-full bg-[#A855F7]/30 blur-[140px] pointer-events-none"
        />
        <motion.div
          style={{ y: heroY }}
          className="absolute top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#7E22CE]/25 blur-[120px] pointer-events-none"
        />

        <div className="gf-container relative z-10 pt-16 lg:pt-24 pb-20 lg:pb-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="text-white"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-[#C084FC] mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              {t("hero.badge")}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] tracking-tighter">
              <span className="block text-gradient-purple neon-text">{t("hero.title_a")}</span>
              <span className="block">{t("hero.title_b")}</span>
              <span className="block">{t("hero.title_c")}</span>
            </h1>
            <p className="mt-6 text-base lg:text-lg text-slate-400 max-w-lg leading-relaxed">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button onClick={() => navigate("/catalog")} size="lg" className="btn-neon h-12 px-7 rounded-full font-semibold" data-testid="hero-shop-now">
                {t("common.shop_now")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate("/catalog?flash=1")}
                size="lg"
                variant="outline"
                className="h-12 px-7 rounded-full font-semibold border-white/20 bg-white/5 text-white hover:bg-white/10"
                data-testid="hero-explore-deals"
              >
                {t("common.explore_deals")}
              </Button>
            </div>

            {/* Mini stats */}
            <div className="mt-10 flex flex-wrap gap-8 text-sm">
              <div>
                <div className="font-display text-2xl font-bold">2M+</div>
                <div className="text-slate-500 text-xs uppercase tracking-wider">Gamers</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold">24/7</div>
                <div className="text-slate-500 text-xs uppercase tracking-wider">Support</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold">~2min</div>
                <div className="text-slate-500 text-xs uppercase tracking-wider">Delivery</div>
              </div>
            </div>
          </motion.div>

          {/* Cover collage */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="grid grid-cols-3 gap-3 lg:gap-4">
              {(featured.length ? featured : Array.from({ length: 6 })).slice(0, 6).map((p, i) => (
                <motion.div
                  key={p?.id || i}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.08 }}
                  className={`relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 bg-[#171723] ${
                    i % 2 === 0 ? "translate-y-4" : "-translate-y-4"
                  } shadow-[0_24px_60px_rgba(168,85,247,0.3)] animate-float`}
                  style={{ animationDelay: `${i * 0.5}s` }}
                >
                  {p ? (
                    <>
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2 text-white text-[10px] font-display font-semibold line-clamp-1">
                        {p.title}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#A855F7]/20 to-[#7E22CE]/30 animate-pulse" />
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="relative -mt-12 z-10 px-4">
        <div className="gf-container">
          <div className="rounded-2xl bg-white shadow-[0_24px_60px_rgba(15,15,20,0.08)] border border-slate-100 p-6 lg:p-8 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="trust-strip">
            {[
              { icon: Zap, ...t("home.strip.instant") },
              { icon: ShieldCheck, ...t("home.strip.secure") },
              { icon: Tag, ...t("home.strip.prices") },
              { icon: Headphones, ...t("home.strip.support") },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-[#A855F7]/10 grid place-items-center text-[#A855F7] shrink-0">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display font-semibold text-sm text-slate-900">{item.t}</div>
                  <div className="text-xs text-slate-500">{item.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <Section title={t("home.featured_games")} viewAll="/catalog">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
          {featured.slice(0, 6).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </Section>

      {/* CATEGORIES STRIP */}
      <section className="py-8" data-testid="categories-strip">
        <div className="gf-container">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {categories.map((c, i) => {
              const Icon = ICONS[c.icon] || Gamepad2;
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link to={`/catalog?category=${encodeURIComponent(c.name)}`} className="block">
                    <div className="rounded-2xl bg-white border border-slate-200 p-4 hover:border-[#A855F7]/40 hover:shadow-neon-soft transition-all duration-300 hover:-translate-y-0.5 group">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#A855F7]/10 to-[#C084FC]/20 grid place-items-center mb-3 group-hover:from-[#A855F7] group-hover:to-[#7E22CE] transition-all">
                        <Icon className="w-5 h-5 text-[#A855F7] group-hover:text-white transition-colors" />
                      </div>
                      <div className="font-display font-semibold text-sm text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">Ver produtos</div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLASH DEALS WITH COUNTDOWN */}
      <section className="py-16" data-testid="flash-deals-section">
        <div className="gf-container">
          <div className="rounded-3xl bg-gradient-to-br from-[#1F1F2E] via-[#171723] to-[#0F0F14] p-6 sm:p-10 relative overflow-hidden">
            <div className="absolute inset-0 hero-grid opacity-50 pointer-events-none" />
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#A855F7]/20 rounded-full blur-[100px]" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-5 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 text-[#C084FC] text-xs font-semibold uppercase tracking-wider mb-2">
                  <Clock className="w-3.5 h-3.5" /> {t("home.flash_deals")}
                </div>
                <h2 className="font-display text-3xl lg:text-4xl font-bold text-white">{t("home.flash_deals")}</h2>
              </div>
              <CountdownTimer />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 relative">
              {flashDeals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </div>
        </div>
      </section>

      {/* TRENDING */}
      <Section title={t("home.trending")} viewAll="/catalog?trending=1">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {trending.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </Section>

      {/* GIFT CARDS + SUBSCRIPTIONS */}
      <section className="py-16" data-testid="gift-subs-section">
        <div className="gf-container grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Panel title={t("home.gift_cards")} link="/catalog?type=gift_card" items={giftCards.slice(0, 4)} />
          <Panel title={t("home.subscriptions")} link="/catalog?type=subscription" items={subscriptions.slice(0, 4)} />
        </div>
      </section>

      {/* TOP SELLERS */}
      <Section title={t("home.top_sellers")} viewAll="/catalog?top=1">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {topSellers.slice(0, 5).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </Section>

      {/* NEW RELEASES */}
      <Section title={t("home.new_releases")} viewAll="/catalog?new=1">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {newReleases.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </Section>

      {/* REVIEWS */}
      <ReviewsSection />

      {/* NEWSLETTER */}
      <section className="py-16">
        <div className="gf-container">
          <div className="rounded-3xl bg-gradient-to-br from-[#A855F7] via-[#9333EA] to-[#7E22CE] p-8 sm:p-14 text-center text-white relative overflow-hidden" data-testid="newsletter-section">
            <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-3">{t("home.newsletter_title")}</h2>
            <p className="text-white/80 max-w-xl mx-auto mb-6">{t("home.newsletter_sub")}</p>
            <form className="max-w-md mx-auto flex gap-2" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="email@example.com"
                className="flex-1 h-12 px-5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-white placeholder:text-white/60 focus:outline-none focus:bg-white/25"
                data-testid="newsletter-email"
              />
              <button type="submit" className="h-12 px-6 rounded-full bg-white text-[#A855F7] font-semibold hover:bg-slate-100 transition-colors" data-testid="newsletter-submit">
                {t("home.newsletter_cta")}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, viewAll, children }) {
  const { t } = useI18n();
  return (
    <section className="py-12">
      <div className="gf-container">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">{title}</h2>
          {viewAll && (
            <Link to={viewAll} className="text-sm font-semibold text-[#A855F7] hover:underline flex items-center gap-1" data-testid={`view-all-${title}`}>
              {t("common.see_all")} <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
        {children}
      </div>
    </section>
  );
}

function Panel({ title, link, items }) {
  const { t } = useI18n();
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-xl font-bold text-slate-900">{title}</h3>
        <Link to={link} className="text-sm font-semibold text-[#A855F7] hover:underline">{t("common.see_all")}</Link>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>
    </div>
  );
}

function ReviewsSection() {
  const { t } = useI18n();
  const reviews = [
    { name: "Lucas M.", role: "PS5 Player", quote: "Code arrived in less than 2 minutes. The UI is gorgeous and prices unbeatable.", rating: 5, avatar: "https://i.pravatar.cc/100?img=12" },
    { name: "Mariana S.", role: "PC Gamer", quote: "The most polished gaming store I've used. Smooth checkout and instant delivery.", rating: 5, avatar: "https://i.pravatar.cc/100?img=47" },
    { name: "Ricardo F.", role: "Esports Fan", quote: "Bought a Game Pass key — worked first try. Customer support is also top-tier.", rating: 5, avatar: "https://i.pravatar.cc/100?img=33" },
  ];
  return (
    <section className="py-16" data-testid="reviews-section">
      <div className="gf-container">
        <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 text-center mb-3">{t("home.reviews")}</h2>
        <p className="text-slate-500 text-center mb-10 max-w-xl mx-auto">Trusted by 2M+ gamers worldwide.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-neon-soft hover:border-[#A855F7]/30 transition-all"
            >
              <Quote className="w-8 h-8 text-[#A855F7]/20 mb-3" />
              <p className="text-slate-700 leading-relaxed mb-5">"{r.quote}"</p>
              <div className="flex items-center gap-3">
                <img src={r.avatar} alt={r.name} className="w-10 h-10 rounded-full" />
                <div>
                  <div className="font-semibold text-sm text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.role}</div>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
